import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import AppLayout from '../layouts/AppLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import AuthGuard from '../guards/AuthGuard';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    // Main Routes
    {
      path: '*',
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      path: '/pdf',
      element: (
        <AuthGuard>
          <Pdf />
        </AuthGuard>
      )
    },
    {
      path: '/profile',
      element: (
        <AuthGuard>
          <AppLayout>
            <Profile />
          </AppLayout>
        </AuthGuard>
      )
    },
    {
      path: '/stats',
      element: (
        <AuthGuard>
          <AppLayout>
            <Stats />
          </AppLayout>
        </AuthGuard>
      )
    },
    {
      path: '/per-mutuelle',
      element: (
        <AuthGuard>
          <AppLayout>
            <PerMutuellePage />
          </AppLayout>
        </AuthGuard>
      )
    },
    {
      path: '/per-mutuelle-v2',
      element: (
        <AuthGuard>
          <AppLayout>
            <PerMutuellePageV2 />
          </AppLayout>
        </AuthGuard>
      )
    },
    {
      path: '/admin',
      element: (
        <AuthGuard>
          <AppLayout>
            <AdminPage />
          </AppLayout>
        </AuthGuard>
      )
    },
    {
      path: '/reset-password/:token',
      element: <ResetPassword />
    },
    {
      path: '/request-reset-password',
      element: <RequestResetPassword />
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: (
            <AuthGuard>
              <LandingPage />
            </AuthGuard>
          )
        }
      ]
    },
    {
      path: '/patiente/:id',
      element: (
        <AuthGuard>
          <AppLayout>
            <PatientePage />
          </AppLayout>
        </AuthGuard>
      )
    },
    {
      path: '/visit/:patienteId/:id',
      element: (
        <AuthGuard>
          <AppLayout>
            <VisitPage />
          </AppLayout>
        </AuthGuard>
      )
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// IMPORT COMPONENTS

// Dashboard
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
// Main
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
const PatientePage = Loadable(lazy(() => import('../pages/PatientePage')));
const VisitPage = Loadable(lazy(() => import('../pages/VisitPage')));
const AdminPage = Loadable(lazy(() => import('../pages/Admin')));
const Stats = Loadable(lazy(() => import('../pages/Stats')));
const Profile = Loadable(lazy(() => import('../pages/Profile')));
const ResetPassword = Loadable(
  lazy(() => import('../pages/authentication/ResetPassword'))
);
const RequestResetPassword = Loadable(
  lazy(() => import('../pages/authentication/RequestResetPassword'))
);
const PerMutuellePage = Loadable(
  lazy(() => import('../pages/PerMutuellePage'))
);
const PerMutuellePageV2 = Loadable(
  lazy(() => import('../pages/PerMutuellePageV2'))
);
const Pdf = Loadable(
  lazy(() => import('../pages/Profile'))
);
